import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronUp, Close, Search } from "@dreamhub/designsystem/icons";
import { IconButton } from "@/components/ui/icon-button";
import IconContainer from "@/components/ui/icon-container";
import Text from "@/components/ui/text";
import { DreamerSearch } from "./useDreamerSearch";

const TRANSLATION_KEY = "chat.search";

interface DreamerSearchBarProps {
  search: DreamerSearch;
  onClose: () => void;
}

export const DreamerSearchBar: React.FC<DreamerSearchBarProps> = ({
  search,
  onClose
}) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const {
    term,
    setTerm,
    clear,
    matchCount,
    activePosition,
    goToNext,
    goToPrevious
  } = search;

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const hasTerm = term.trim().length > 0;
  const hasResults = matchCount > 0;
  const showNoResults = hasTerm && !hasResults;

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      if (event.shiftKey) {
        goToPrevious();
      } else {
        goToNext();
      }
    } else if (event.key === "Escape") {
      event.preventDefault();
      onClose();
    }
  };

  return (
    <div
      className="flex flex-col gap-1 border-b border-border-border1"
      data-test="dreamer-search-bar"
    >
      <div className="flex items-center gap-2 px-2 pt-2">
        <div className="flex flex-1 items-center gap-2 min-w-0 h-9 p-2 rounded-lg bg-surface-field-default border-[0.5px] border-border-field-default shadow-field focus-within:border-border-field-focus focus-within:bg-surface-focus-selected focus-within:shadow-field-focus transition-all duration-200 ease-in-out">
          <IconContainer size="5" color="tertiary">
            <Search />
          </IconContainer>
          <input
            ref={inputRef}
            type="search"
            aria-label={t(`${TRANSLATION_KEY}.placeholder`)}
            value={term}
            onChange={(event) => setTerm(event.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t(`${TRANSLATION_KEY}.placeholder`)}
            autoComplete="off"
            spellCheck={false}
            className="flex-1 min-w-0 bg-transparent border-none focus:outline-none text-base text-primary placeholder:text-tertiary [&::-webkit-search-cancel-button]:hidden"
            data-test="dreamer-search-input"
          />
          <IconButton
            variant="iconNoBg"
            iconColor="secondary"
            iconClassName="size-4"
            size="sm"
            onClick={clear}
            disabled={!hasTerm}
            aria-label={t(`${TRANSLATION_KEY}.clear`)}
            data-test="dreamer-search-clear"
          >
            <Close />
          </IconButton>
        </div>
        <IconButton
          variant="iconNoBg"
          size="md"
          onClick={onClose}
          aria-label={t(`${TRANSLATION_KEY}.close`)}
          data-test="dreamer-search-back"
        >
          <Close />
        </IconButton>
      </div>

      <div className="flex items-center justify-between gap-2 pl-4 pr-2 py-1">
        {hasTerm ? (
          <Text
            variant="smBodySecondary"
            className="whitespace-nowrap"
            data-test="dreamer-search-counter"
          >
            {showNoResults
              ? t(`${TRANSLATION_KEY}.noResults`)
              : t(`${TRANSLATION_KEY}.matches`, {
                  current: activePosition,
                  total: matchCount
                })}
          </Text>
        ) : (
          <div />
        )}

        <div className="flex items-center gap-1">
          <IconButton
            variant="iconNoBg"
            size="md"
            iconColor="secondary"
            onClick={goToPrevious}
            disabled={!hasResults}
            aria-label={t(`${TRANSLATION_KEY}.previous`)}
            data-test="dreamer-search-previous"
          >
            <ChevronUp />
          </IconButton>
          <IconButton
            variant="iconNoBg"
            size="md"
            iconColor="secondary"
            onClick={goToNext}
            disabled={!hasResults}
            aria-label={t(`${TRANSLATION_KEY}.next`)}
            data-test="dreamer-search-next"
          >
            <ChevronDown />
          </IconButton>
        </div>
      </div>
    </div>
  );
};
